<template>
  <WebLayout
    title="Sistema de Cobranzas Automatizado"
    description="Agiliza tu proceso de recuperación de cartera con el sistema de cobranzas de Realgestión. Conciliación automática, portal de clientes y reportes de pago en línea."
    url="https://realgestion.lat/sistema-cobranzas"
  >
    <Head :title="`Realgestión - Sistema de Cobranzas`" />
    
    <!-- Hero Section Banner -->
    <div class="w-full bg-[#fcfcfc]">
      <div class="w-full">
        <Link href="/register" class="block w-full cursor-pointer hover:opacity-95 transition-opacity">
          <!-- The image is served natively since it's inside public/img -->
          <img src="/img/rg_portada_facebook.png" alt="Sistema de Cobranzas Diseñado para: Condominios, Academias, Profesionales, Freelancers" class="w-full h-auto object-cover" />
        </Link>
      </div>

      <!-- Call to Action Buttons -->
      <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex flex-col sm:flex-row justify-center gap-4 relative z-10">
        <Link href="/register" class="w-full sm:w-auto flex items-center justify-center px-8 py-4 border border-transparent text-base font-bold rounded-lg text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-xl sm:text-lg sm:px-12 transform hover:-translate-y-1 transition-all duration-200">
          <i class="fas fa-rocket mr-3" /> Comenzar prueba gratis
        </Link>
        <a href="#features" class="w-full sm:w-auto flex items-center justify-center px-8 py-4 border-2 border-gray-800 text-base font-bold rounded-lg text-gray-800 bg-transparent hover:bg-gray-800 hover:text-white shadow-lg sm:text-lg sm:px-12 transform hover:-translate-y-1 transition-all duration-200">
          <i class="fas fa-list-ul mr-3" /> Ver funcionalidades
        </a>
      </div>
    </div>

    <!-- Features Section -->
    <div id="features" class="py-12 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="lg:text-center">
          <h2 class="text-base text-orange-600 font-semibold tracking-wide uppercase">Funcionalidades de Cobranza</h2>
          <p class="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Todo lo que necesitas para tu departamento de crédito
          </p>
          <p class="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Herramientas poderosas y portales de autogestión para mantener la morosidad a raya.
          </p>
        </div>

        <div class="mt-10">
          <div class="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
            <div v-for="(feature, index) in features" :key="index" class="relative">
              <div class="absolute flex items-center justify-center h-12 w-12 rounded-md bg-orange-500 text-white">
                <i :class="feature.icon + ' text-xl'" />
              </div>
              <p class="ml-16 text-lg leading-6 font-medium text-gray-900">{{ feature.title }}</p>
              <p class="mt-2 ml-16 text-base text-gray-500" v-html="renderMarkdown(feature.description)" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Comparison Section -->
    <div v-if="comparison" class="py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="lg:text-center mb-12">
          <h2 class="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            ¿Por qué elegir nuestro sistema de Cobranzas?
          </h2>
          <p class="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Compara y descubre la gran diferencia a la hora de gestionar cuentas por cobrar.
          </p>
        </div>
        
        <div class="flex flex-col">
          <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div class="shadow-xl overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        v-for="(header, index) in comparison.headers" :key="index" scope="col" 
                        class="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider"
                        :class="{'bg-orange-100 text-orange-800 border-b-2 border-orange-500': index === 1, 'text-gray-500': index !== 1}"
                      >
                        <div class="flex items-center">
                          {{ header }}
                          <span v-if="index === 1" class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-200 text-orange-800">
                            Sugerido
                          </span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(row, rowIndex) in comparison.rows" :key="rowIndex" class="hover:bg-gray-50 transition-colors duration-150">
                      <td
                        v-for="(cell, cellIndex) in row" :key="cellIndex" 
                        class="px-6 py-4 whitespace-nowrap text-sm"
                        :class="{'bg-orange-50 font-medium text-gray-900': cellIndex === 1, 'text-gray-500': cellIndex !== 1}"
                      >
                        <span v-if="cell === 'Sí'">
                          <i class="fas fa-check-circle text-green-500 text-lg" />
                          <span class="sr-only">Sí</span>
                        </span>
                        <span v-else-if="cell === 'No' || cell === 'No disponible'">
                          <i class="fas fa-times-circle text-red-400 text-lg" />
                          <span v-if="cell !== 'No'" class="ml-2">{{ cell }}</span>
                        </span>
                        <span v-else>
                          {{ cell }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="bg-gray-50 py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <blockquote class="text-2xl font-medium text-gray-900 italic">
          "Minimiza la fricción en los pagos y dale autonomía a tus clientes. La cobranza ya no tiene que ser un dolor de cabeza, hazla inteligente con Realgestion."
        </blockquote>
      </div>
    </div>
  </WebLayout>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import WebLayout from '@/Layouts/WebLayout.vue'

export default {
  components: {
    Link,
    Head,
    WebLayout,
  },
  props: {
    features: {
      type: Array,
      default: () => [],
    },
    comparison: {
      type: Object,
      default: () => null,
    },
  },
  methods: {
    renderMarkdown(text) {
      if (!text) return ''
      return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    },
  },
}
</script>
